
import { supabase } from "@/integrations/supabase/client";

// In-memory cache for profile data to avoid repeated database calls
const profileCache = new Map();

/**
 * Fetch user profile from Supabase with caching
 */
export const fetchUserProfile = async (userId: string) => {
  try {
    if (!userId) {
      console.error('Cannot fetch user profile: No userId provided');
      return null;
    }
    
    // Check memory cache first
    if (profileCache.has(userId)) {
      const cachedProfile = profileCache.get(userId);
      const cacheTimestamp = cachedProfile._cachedAt || 0;
      
      // Use cache if it's less than 5 minutes old
      if ((Date.now() - cacheTimestamp) < 300000) {
        return cachedProfile;
      }
    }
    
    // Fetch user profile from profiles table
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching user profile:', error);
      return null;
    }
    
    if (data) {
      // Add cache timestamp and store in memory
      const profileWithTimestamp = {...data, _cachedAt: Date.now()};
      profileCache.set(userId, profileWithTimestamp);
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

/**
 * Format user profile data for the frontend
 */
export const formatUserProfile = (profile: any) => {
  if (!profile) return null;
  
  try {
    // Format profile data for frontend consumption
    return {
      name: profile.name || '',
      createdAt: profile.created_at || new Date().toISOString(),
      credit: Number(profile.credit || 0),
      // Add any other fields as needed
    };
  } catch (error) {
    console.error('Error formatting user profile:', error);
    return null;
  }
};

/**
 * Create a new user profile in Supabase
 */
export const createUserProfile = async (userId: string, name: string) => {
  try {
    if (!userId) {
      console.error('Cannot create user profile: No userId provided');
      return false;
    }
    
    // Verify current user authentication without accessing auth.users table
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user || user.id !== userId) {
      console.error('Authentication error or user mismatch:', authError);
      return false;
    }
    
    // Create user profile in profiles table
    const { error } = await supabase
      .from('profiles')
      .insert({
        id: userId,
        name: name,
        credit: 0
      });
    
    if (error) {
      console.error('Error creating user profile:', error);
      return false;
    }
    
    // Clear cache for this user to ensure we fetch the latest data next time
    profileCache.delete(userId);
    
    return true;
  } catch (error) {
    console.error('Error creating user profile:', error);
    return false;
  }
};

/**
 * Update user name in profile
 */
export const updateUserName = async (userId: string, name: string) => {
  try {
    if (!userId) {
      console.error('Cannot update user name: No userId provided');
      return false;
    }
    
    // Verify current user authentication without accessing auth.users table
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user || user.id !== userId) {
      console.error('Authentication error or user mismatch:', authError);
      return false;
    }
    
    // Try to update existing profile first
    const { data: updateData, error: updateError } = await supabase
      .from('profiles')
      .update({ name })
      .eq('id', userId)
      .select('id, name');
    
    if (updateError) {
      console.error('Error updating user name, trying to create profile:', updateError);
      
      // If update failed (profile doesn't exist), try to insert a new profile
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          name: name,
          credit: 0
        });
      
      if (insertError) {
        console.error('Error creating user profile:', insertError);
        return false;
      }
      
      console.log('Successfully created profile with name:', name);
    } else {
      console.log('Successfully updated user name:', updateData);
    }
    
    // Clear cache for this user to ensure we fetch the latest data next time
    profileCache.delete(userId);
    
    return true;
  } catch (error) {
    console.error('Error updating user name:', error);
    return false;
  }
};

/**
 * Clear profile cache for testing or when needed
 */
export const clearProfileCache = () => {
  profileCache.clear();
};
